import { useEffect, useState, useRef, useMemo } from 'react';
import { X, Building2, Target, Server, Users, CheckCircle2, AlertTriangle, GraduationCap, Calendar, TrendingUp, Zap, Download, FileDown, Shield, Lock, FileText } from 'lucide-react';
import { getBesoinById, type BesoinData } from '../services/besoinsService';
import { getAllClients } from '../services/clientService';
import type { ClientItem } from '../services/clientService';
import { METIERS } from '../domain/metier/metierTypes';
import { getIntakeTemplate } from '../domain/besoins/templates';
import * as XLSX from 'xlsx';
import { domToPng } from 'modern-screenshot';
import { jsPDF } from 'jspdf';

interface BesoinRapportModalProps {
  isOpen: boolean;
  onClose: () => void;
  besoinId: number;
}

// Mapping des icônes par nom
const iconMap: Record<string, any> = {
  Building2,
  Target,
  Server,
  FileText,
  Briefcase: GraduationCap,
  Shield,
  Lock,
};

export default function BesoinRapportModal({ isOpen, onClose, besoinId }: BesoinRapportModalProps) {
  const [besoin, setBesoin] = useState<BesoinData | null>(null);
  const [client, setClient] = useState<ClientItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  // Charger le template basé sur le template_id du besoin
  const template = useMemo(() => {
    if (!besoin?.template_id) return null;
    try {
      return getIntakeTemplate(besoin.template_id);
    } catch (error) {
      console.error('Erreur lors du chargement du template:', error);
      return null;
    }
  }, [besoin?.template_id]);

  useEffect(() => {
    if (isOpen && besoinId) {
      loadBesoin();
    }
  }, [isOpen, besoinId]);

  const loadBesoin = async () => {
    setIsLoading(true);
    try {
      const besoinData = await getBesoinById(besoinId);
      setBesoin(besoinData);

      if (besoinData?.client_id) {
        const clients = await getAllClients();
        const foundClient = clients.find((c) => c.id === besoinData.client_id);
        setClient(foundClient || null);
      }
    } catch (error) {
      console.error('Erreur chargement besoin:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getMetierLabel = (metierId?: string) => {
    if (!metierId) return null;
    const metier = METIERS.find((m) => m.id === metierId);
    return metier?.label || metierId;
  };

  const formatArrayValue = (value: any): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return [value];
    return [];
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Non spécifié';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
  };

  const handleExportExcel = async () => {
    if (!besoin || !client) return;
    
    setIsExporting(true);
    try {
      const phase1 = besoin.phase1_data || {};
      const phase2 = besoin.phase2_data || {};
      const phase3 = besoin.phase3_data || {};
      const phase4 = besoin.phase4_data || {};
      const phase5Metier = besoin.phase5_metier_data || {};

      // Créer un workbook
      const wb = XLSX.utils.book_new();

      // Feuille 1: Informations générales
      const generalData = [
        ['Analyse de Besoins TI – PRIsME'],
        [],
        ['Nom de la prise de besoin', besoin.nom || 'N/A'],
        ['Client', client.nom || `Client #${besoin.client_id}`],
        ['Métier', metierLabel || 'N/A'],
        ['Statut', besoin.statut || 'N/A'],
        ['Complétion', `${completion}%`],
        ['Date de création', besoin.created_at ? new Date(besoin.created_at).toLocaleDateString('fr-CA') : 'N/A'],
        [],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(generalData);
      XLSX.utils.book_append_sheet(wb, ws1, 'Informations générales');

      // Feuille 2: Phase 1 - Qualification
      if (Object.keys(phase1).length > 0) {
        const phase1Data = [
          ['Phase 1: Qualification Client'],
          [],
          ['Type d\'entreprise', formatValue(phase1.company_type)],
          ['Industrie / Secteur', formatValue(phase1.industry)],
          ['Nombre d\'employés', formatValue(phase1.employees)],
          ['Chiffre d\'affaires', formatValue(phase1.revenue)],
          ['Budget TI', formatValue(phase1.budget_range)],
          ['Maturité TI', formatValue(phase1.it_maturity)],
        ];
        const ws2 = XLSX.utils.aoa_to_sheet(phase1Data);
        XLSX.utils.book_append_sheet(wb, ws2, 'Phase 1');
      }

      // Feuille 3: Phase 2 - Contexte & Objectifs
      if (Object.keys(phase2).length > 0) {
        const phase2Data = [
          ['Phase 2: Contexte & Objectifs'],
          [],
          ['Défi Principal', formatValue(phase2.main_challenge)],
          [],
          ['Objectifs d\'Affaires'],
          ...formatArrayValue(phase2.business_goals).map(g => [g]),
          [],
          ['Objectifs TI'],
          ...formatArrayValue(phase2.it_goals).map(g => [g]),
          [],
          ['Timeline', formatValue(phase2.timeline)],
          ['Budget estimé', formatValue(phase2.budget_range)],
          [],
          ['Métriques de Succès', formatValue(phase2.success_metrics)],
        ];
        const ws3 = XLSX.utils.aoa_to_sheet(phase2Data);
        XLSX.utils.book_append_sheet(wb, ws3, 'Phase 2');
      }

      // Feuille 4: Phase 3 - Besoins Techniques
      if (Object.keys(phase3).length > 0) {
        const phase3Data = [
          ['Phase 3: Besoins Techniques'],
          [],
          ['Infrastructure Actuelle', formatValue(phase3.current_infrastructure)],
          ['Applications Critiques', formatValue(phase3.applications)],
          ['Besoins d\'Intégration', formatValue(phase3.integration_needs)],
          ['Préoccupations Sécurité', formatValue(phase3.security_concerns)],
        ];
        const ws4 = XLSX.utils.aoa_to_sheet(phase3Data);
        XLSX.utils.book_append_sheet(wb, ws4, 'Phase 3');
      }

      // Feuille 5: Phase 4 - Gouvernance & Support
      if (Object.keys(phase4).length > 0) {
        const phase4Data = [
          ['Phase 4: Gouvernance & Support'],
          [],
          ['Modèle de Support', formatValue(phase4.support_model)],
          ['Structure de Gouvernance', formatValue(phase4.governance_structure)],
          ['Besoins de Formation', formatValue(phase4.training_needs)],
        ];
        const ws5 = XLSX.utils.aoa_to_sheet(phase4Data);
        XLSX.utils.book_append_sheet(wb, ws5, 'Phase 4');
      }

      // Feuille 6: Phase 5 - Métier
      if (metierLabel && Object.keys(phase5Metier).length > 0) {
        const phase5Data = [
          [`Phase 5: Page Métier – ${metierLabel}`],
          [],
          ['Résumé des Activités', formatValue(phase5Metier.resume_activites)],
          [],
          ['Processus Clés'],
          ...formatArrayValue(phase5Metier.processus_cles).map(p => [p]),
          [],
          ['Enjeux Principaux'],
          ...formatArrayValue(phase5Metier.enjeux_principaux).map(e => [e]),
        ];
        const ws6 = XLSX.utils.aoa_to_sheet(phase5Data);
        XLSX.utils.book_append_sheet(wb, ws6, 'Phase 5');
      }

      // Générer le fichier
      const fileName = `Prise_de_besoin_${besoin.nom || besoin.id}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Erreur export Excel:', error);
      alert('Erreur lors de l\'export Excel. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!reportRef.current || !besoin) return;
    
    setIsExporting(true);
    try {
      const element = reportRef.current;
      
      // Capturer l'élément avec modern-screenshot
      const dataUrl = await domToPng(element, {
        scale: 2,
        width: element.scrollWidth,
        height: element.scrollHeight,
        backgroundColor: '#f9fafb',
      });

      // Convertir dataUrl en image pour obtenir les dimensions
      const img = new Image();
      img.src = dataUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      // Calculer les dimensions pour le PDF
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (img.height * imgWidth) / img.width;
      
      // Marges
      const topMargin = 10;
      const bottomMargin = 10;
      const pageContentHeight = pageHeight - topMargin - bottomMargin;

      // Créer le PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Si l'image tient sur une seule page
      if (imgHeight <= pageContentHeight) {
        pdf.addImage(dataUrl, 'PNG', 0, topMargin, imgWidth, imgHeight);
      } else {
        // Multi-pages
        let heightLeft = imgHeight;
        let position = topMargin;
        
        pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageContentHeight;
        
        while (heightLeft > 0) {
          position = -pageContentHeight + heightLeft;
          pdf.addPage();
          pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageContentHeight;
        }
      }

      // Sauvegarder
      const fileName = `Prise_de_besoin_${besoin.nom || besoin.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <p className="text-sm text-gray-600">Chargement du rapport...</p>
        </div>
      </div>
    );
  }

  if (!besoin) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <p className="text-sm text-red-600">Erreur: Prise de besoin non trouvée</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-bz-blue text-white rounded-lg">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  const phase1 = besoin.phase1_data || {};
  const phase2 = besoin.phase2_data || {};
  const phase3 = besoin.phase3_data || {};
  const phase4 = besoin.phase4_data || {};
  const phase5Metier = besoin.phase5_metier_data || {};

  const completion = besoin.metadata?.completion_percentage || 0;
  const metierLabel = getMetierLabel(besoin.metier_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div className="w-[85vw] max-w-[1500px] max-h-[95vh] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header avec gradient coloré */}
        <div className="bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-600 text-white px-6 py-5 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shadow-inner">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Analyse de Besoins TI – PRIsME</h2>
              <p className="text-sm text-white/90 font-medium">
                {besoin.nom || 'Prise de besoin'} • {client?.nom || `Client #${besoin.client_id}`}
                {metierLabel && ` • ${metierLabel}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportExcel}
              disabled={isExporting}
              className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Export...' : 'Excel'}
            </button>
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {isExporting ? 'Export...' : 'PDF'}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  completion === 100
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600'
                    : completion >= 50
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600'
                    : 'bg-gradient-to-r from-gray-400 to-gray-500'
                }`}
                style={{ width: `${completion}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-700 min-w-[60px] text-right">
              {completion}% complété
            </span>
          </div>
          <div className="flex items-center gap-4 ml-4 text-xs">
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{besoin.created_at ? new Date(besoin.created_at).toLocaleDateString('fr-CA') : 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 rounded-full font-semibold ${
                  besoin.statut === 'complet'
                    ? 'bg-emerald-100 text-emerald-700'
                    : besoin.statut === 'approuve_client'
                    ? 'bg-indigo-100 text-indigo-700'
                    : besoin.statut === 'approuve_prisme'
                    ? 'bg-purple-100 text-purple-700'
                    : besoin.statut === 'en_cours'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {besoin.statut === 'complet'
                  ? '✅ Complété'
                  : besoin.statut === 'approuve_client'
                  ? '✅ Approuvé client'
                  : besoin.statut === 'approuve_prisme'
                  ? '✅ Approuvé PRIsME'
                  : besoin.statut === 'en_cours'
                  ? '🔄 En cours'
                  : '📝 Brouillon'}
              </span>
            </div>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div ref={reportRef} className="flex-1 overflow-y-auto px-6 py-5 bg-gradient-to-br from-gray-50 to-blue-50/30 space-y-5">
          {/* Informations client - Adaptatif selon le template */}
          <div className={`rounded-xl p-5 shadow-lg text-white ${
            template?.id === 'politique-cybersecurite-v2' 
              ? 'bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500'
              : 'bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-600'
          }`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {template?.id === 'politique-cybersecurite-v2' ? (
                <>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Building2 className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Entreprise</p>
                      <p className="font-semibold">{formatValue(phase1.entreprise) || client?.nom || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Shield className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Enquête sécurité</p>
                      <p className="font-semibold">{formatValue(phase1.enquete_securite_utilisateurs) || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <GraduationCap className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Formation obligatoire</p>
                      <p className="font-semibold">{formatValue(phase1.formation_obligatoire_utilisateurs) || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Users className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Groupes utilisateurs</p>
                      <p className="font-semibold">{formatValue(phase1.groupes_utilisateurs_uniformes) || 'Non spécifié'}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Building2 className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Organisation</p>
                      <p className="font-semibold">{formatValue(phase1.company_name) || client?.nom || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Users className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Taille</p>
                      <p className="font-semibold">{formatValue(phase1.employees) || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Target className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Timeline</p>
                      <p className="font-semibold">{formatValue(phase2.timeline) || 'Non spécifié'}</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 flex items-center gap-2 backdrop-blur-sm">
                    <Server className="w-5 h-5 text-white/90" />
                    <div>
                      <p className="text-[10px] text-white/80 font-medium">Maturité TI</p>
                      <p className="font-semibold">{formatValue(phase1.it_maturity) || 'Non spécifié'}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Affichage dynamique des phases selon le template */}
          {template ? (
            // Utiliser le template chargé
            template.phases.map((phase, phaseIndex) => {
              const phaseNum = phaseIndex + 1;
              const phaseDataKey = `phase${phaseNum}_data` as keyof BesoinData;
              const phaseData = (besoin[phaseDataKey] as Record<string, any>) || {};
              
              if (Object.keys(phaseData).length === 0) return null;

              const colors = [
                { border: 'border-red-500', bg: 'bg-red-500', text: 'text-red-600' },
                { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-orange-600' },
                { border: 'border-yellow-500', bg: 'bg-yellow-500', text: 'text-yellow-600' },
                { border: 'border-emerald-500', bg: 'bg-emerald-500', text: 'text-emerald-600' },
                { border: 'border-blue-500', bg: 'bg-blue-500', text: 'text-blue-600' },
              ];
              const color = colors[phaseIndex] || colors[0];
              const Icon = phase.icon ? (iconMap[phase.icon] || Building2) : Building2;

              return (
                <div key={phase.id} className={`bg-white rounded-xl border-2 ${color.border} p-5 shadow-lg`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-xl ${color.bg} flex items-center justify-center`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className={`text-xs font-semibold ${color.text} uppercase`}>Phase {phaseNum}</p>
                      <h3 className="text-base font-bold text-gray-900">{phase.title}</h3>
                      {phase.description && (
                        <p className="text-xs text-gray-600 mt-0.5">{phase.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {phase.questions.map((q) => {
                      const value = phaseData[q.id];
                      if (value === null || value === undefined || value === '') return null;

                      return (
                        <div key={q.id}>
                          <p className="text-xs font-semibold text-gray-600 mb-1">{q.label}</p>
                          <p className="text-gray-900 font-medium">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            // Fallback sur l'affichage hardcodé pour pdti-standard-pme
            <>
              {/* Phase 1: Qualification */}
              {Object.keys(phase1).length > 0 && (
            <div className="bg-white rounded-xl border-2 border-blue-200 p-5 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-blue-600 uppercase">Phase 1</p>
                  <h3 className="text-base font-bold text-gray-900">Qualification Client</h3>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Type d'entreprise</p>
                  <p className="text-gray-900 font-medium">{formatValue(phase1.company_type)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Industrie / Secteur</p>
                  <p className="text-gray-900 font-medium">{formatValue(phase1.industry)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Chiffre d'affaires</p>
                  <p className="text-gray-900 font-medium">{formatValue(phase1.revenue)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Budget TI</p>
                  <p className="text-gray-900 font-medium">{formatValue(phase1.budget_range)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Phase 2: Contexte & Objectifs - Regroupée */}
          {Object.keys(phase2).length > 0 && (
            <div className="bg-white rounded-xl border-2 border-orange-200 p-5 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-orange-600 uppercase">Phase 2</p>
                  <h3 className="text-base font-bold text-gray-900">Contexte & Objectifs</h3>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Défi principal */}
                {phase2.main_challenge && (
                  <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="text-sm font-bold text-red-900">Défi Principal à Relever</h4>
                    </div>
                    <p className="text-sm text-red-800 leading-relaxed">{formatValue(phase2.main_challenge)}</p>
                  </div>
                )}

                {/* Objectifs en grille */}
                <div className="grid md:grid-cols-2 gap-4">
                  {phase2.business_goals && (
                    <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-emerald-600" />
                        <h4 className="text-sm font-bold text-emerald-900">Objectifs d'Affaires</h4>
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-xs text-emerald-800">
                        {formatArrayValue(phase2.business_goals).map((goal, idx) => (
                          <li key={idx}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {phase2.it_goals && (
                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-blue-600" />
                        <h4 className="text-sm font-bold text-blue-900">Objectifs TI</h4>
                      </div>
                      <ul className="list-disc list-inside space-y-1 text-xs text-blue-800">
                        {formatArrayValue(phase2.it_goals).map((goal, idx) => (
                          <li key={idx}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Timeline et Budget */}
                {(phase2.timeline || phase2.budget_range) && (
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    {phase2.timeline && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Horizon Temporel</p>
                        <p className="text-gray-900 font-medium">{formatValue(phase2.timeline)}</p>
                      </div>
                    )}
                    {phase2.budget_range && (
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-1">Budget Estimé</p>
                        <p className="text-gray-900 font-medium">{formatValue(phase2.budget_range)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Métriques de succès */}
                {phase2.success_metrics && (
                  <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      <h4 className="text-sm font-bold text-purple-900">Métriques de Succès</h4>
                    </div>
                    <p className="text-sm text-purple-800 leading-relaxed">{formatValue(phase2.success_metrics)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 3: Besoins Techniques */}
          {Object.keys(phase3).length > 0 && (
            <div className="bg-white rounded-xl border-2 border-indigo-200 p-5 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
                  <Server className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-indigo-600 uppercase">Phase 3</p>
                  <h3 className="text-base font-bold text-gray-900">Besoins Techniques</h3>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {phase3.current_infrastructure && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Infrastructure Actuelle</p>
                    <p className="text-gray-900">{formatValue(phase3.current_infrastructure)}</p>
                  </div>
                )}
                {phase3.applications && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Applications Critiques</p>
                    <p className="text-gray-900">{formatValue(phase3.applications)}</p>
                  </div>
                )}
                {phase3.integration_needs && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Besoins d'Intégration</p>
                    <p className="text-gray-900">{formatValue(phase3.integration_needs)}</p>
                  </div>
                )}
                {phase3.security_concerns && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Préoccupations Sécurité</p>
                    <p className="text-gray-900">{formatValue(phase3.security_concerns)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 4: Gouvernance & Support */}
          {Object.keys(phase4).length > 0 && (
            <div className="bg-white rounded-xl border-2 border-orange-200 p-5 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-orange-600 uppercase">Phase 4</p>
                  <h3 className="text-base font-bold text-gray-900">Gouvernance & Support</h3>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {phase4.support_model && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Modèle de Support</p>
                    <p className="text-gray-900 font-medium">{formatValue(phase4.support_model)}</p>
                  </div>
                )}
                {phase4.governance_structure && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Structure de Gouvernance</p>
                    <p className="text-gray-900">{formatValue(phase4.governance_structure)}</p>
                  </div>
                )}
                {phase4.training_needs && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Besoins de Formation</p>
                    <p className="text-gray-900">{formatValue(phase4.training_needs)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

              {/* Phase 5: Métier */}
              {metierLabel && besoin.template_id === 'pdti-standard-pme' && Object.keys(phase5Metier).length > 0 && (
                <div className="bg-fuchsia-50 rounded-xl border-2 border-fuchsia-200 p-5 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <GraduationCap className="w-6 h-6 text-fuchsia-600" />
                    <div>
                      <p className="text-xs font-semibold text-fuchsia-600 uppercase">Phase 5</p>
                      <h3 className="text-base font-bold text-fuchsia-900">
                        Page Métier – {metierLabel}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    {phase5Metier.resume_activites && (
                      <div>
                        <p className="text-xs font-semibold text-fuchsia-700 mb-1">Résumé des Activités</p>
                        <p className="text-fuchsia-900">{formatValue(phase5Metier.resume_activites)}</p>
                      </div>
                    )}
                    {phase5Metier.processus_cles && (
                      <div>
                        <p className="text-xs font-semibold text-fuchsia-700 mb-1">Processus Clés</p>
                        <div className="flex flex-wrap gap-2">
                          {formatArrayValue(phase5Metier.processus_cles).map((proc, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-800 text-xs font-medium"
                            >
                              {proc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {phase5Metier.enjeux_principaux && (
                      <div>
                        <p className="text-xs font-semibold text-fuchsia-700 mb-1">Enjeux Principaux</p>
                        <ul className="list-disc list-inside space-y-1 text-fuchsia-900">
                          {formatArrayValue(phase5Metier.enjeux_principaux).map((enjeu, idx) => (
                            <li key={idx}>{enjeu}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Message de complétion */}
          {completion === 100 && (
            <div className={`text-white rounded-xl p-5 shadow-lg flex items-start gap-4 ${
              template?.id === 'politique-cybersecurite-v2'
                ? 'bg-gradient-to-r from-red-500 to-orange-600'
                : 'bg-gradient-to-r from-emerald-500 to-green-600'
            }`}>
              <div className="mt-1">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white text-lg font-bold shadow-inner">
                  ✓
                </span>
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg mb-1">Prise de besoin complétée</p>
                <p className="text-sm text-white/90">
                  {template?.id === 'politique-cybersecurite-v2' ? (
                    <>
                      Tous les besoins ont été structurés dans PRIsME. Vous pouvez maintenant générer la{' '}
                      <strong>Politique de cybersécurité</strong> complète alignée sur NIST CSF 2.0, ITIL 4, COBIT 2019 et ISO 27001.
                    </>
                  ) : (
                    <>
                      Tous les besoins ont été structurés dans PRIsME. Vous pouvez maintenant générer le{' '}
                      <strong>Cadre Référentiel BZ</strong>, la <strong>Feuille de route annuelle</strong> et le{' '}
                      <strong>Plan Directeur</strong> en utilisant cette prise de besoin comme base commune.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

